import {
  Button,
  TextField,
  Tooltip,
} from "@mui/material/";
import { useContext, useEffect, useState } from "react";
import ControlPointOutlinedIcon from "@mui/icons-material/ControlPointOutlined";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";

import ApiRequestErrorHandler from "../../../../../global/ApiRequestErrorHandler";
import ResponseModal from "../../../../../global/ResponseModal";
import useService from "../../../../../../hooks/useService";
import ApptBillsAutocomplete from "./ApptBills/ApptBillsAutocomplete";
import { StaticBarDispatchContext } from "../StaticBar/context/StaticBarContext";
import { setShouldFetchData } from "../StaticBar/state/actions";

const initialData = {
  service: "",
  price: "",
  pk: "",
};

export default function CreateApptProcedurev2({
  caseInfo,
  selectedAppt,
  selectedBills,
  setOpenAddBill,
  setProCliTrigger,
  proCliTrigger
}) {
  const dispatch = useContext(StaticBarDispatchContext);
  const administeredServiceRepository = useService("repositories/administered-service-repository");
  const providerPk = selectedAppt.provider.pk;
  const [openSuccess, setOpenSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState({
    title: "",
    description: "",
  });
  const [openError, setOpenError] = useState(false);
  const [errorArray, setErrorArray] = useState([]);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState([initialData]);
  const [servicesOptsByIndex, setServicesOptsByIndex] = useState([[]]);
  const [searchLimitsByIndex, setSearchLimitsByIndex] = useState([25]);
  const [searchTermsByIndex, setSearchTermsByIndex] = useState([""]);

  const isEditionMode = Boolean(selectedBills?.length > 0);
  const allowSave = formData.every(({ price, service }) => !!price && !!service);

  function handleChange(field, value, index) {
    setFormData((prev) =>
      prev.map((current, idx) =>
        idx === index ? { ...current, [field]: value } : current
      )
    );
  }

  function handleAddBill() {
    setFormData((prev) => [...prev, initialData]);
    setServicesOptsByIndex((prev) => [...prev, []]);
    setSearchLimitsByIndex((prev) => [...prev, 25]);
    setSearchTermsByIndex((prev) => [...prev, ""]);
  }

  function handleRemoveBill(index) {
    if (formData.length === 1) return;
    setFormData((prev) => prev.filter((_, idx) => idx !== index));
    setServicesOptsByIndex((prev) => prev.filter((_, idx) => idx !== index));
    setSearchLimitsByIndex((prev) => prev.filter((_, idx) => idx !== index));
    setSearchTermsByIndex((prev) => prev.filter((_, idx) => idx !== index));
  }

  function handleErrors(error) {
    const err = error ?? error?.response?.data;
    const errArr = ApiRequestErrorHandler(err);
    setErrorArray(errArr);
    setOpenError(true);
  }

  function handleOpenSuccess() {
    setOpenSuccess(false);
    setOpenAddBill(false);
    setProCliTrigger?.(!proCliTrigger);
    setShouldFetchData(dispatch, true);
  }

  async function handleSubmit() {
    try {
      setLoading(true);
      const bills = [];

      for (const element of formData) {
        const commonData = {
          administeredTo: caseInfo.client.pk,
          administeredBy: selectedAppt.provider.pk,
          appointment: selectedAppt.pk,
          service: element.service,
          price: element.price,
        };

        bills.push(isEditionMode ? { ...commonData, pk: element.pk } : commonData);
      }

      if (isEditionMode) {
        await administeredServiceRepository.updateBulk(bills);
        setSuccessMessage({
          title: "Administered Service Updated",
          description: "Successfully Edited Administered Service",
        });
      } else {
        await administeredServiceRepository.createBulk(bills);
        setSuccessMessage({
          title: "New Administered Service Added",
          description: "Successfully Added Administered Service",
        });
      }

      setOpenSuccess(true);
    } catch (error) {
      handleErrors(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!isEditionMode || !selectedBills?.length) return;

    async function initializeForm() {
      const initialFormData = [];

      for (let idx = 0; idx < selectedBills.length; idx++) {
        const bill = selectedBills[idx];
        const serviceValueOpt = bill?.service?.pk;
        const serviceLabelOpt = bill?.service
          ? `${bill.service.service_code} - ${bill.service.service_name}`
          : "";

        setServicesOptsByIndex((prev) => {
          const updated = [...prev];
          updated[idx] = serviceValueOpt ? [{
            value: serviceValueOpt,
            label: serviceLabelOpt,
          }] : [];
          return updated;
        });

        initialFormData.push({
          service: serviceValueOpt,
          price: bill.price,
          pk: bill.pk,
        });
      }

      setFormData(initialFormData);
    }

    initializeForm();
  }, [isEditionMode, selectedBills]);

  return (
    <div className="flex flex-wrap w-full h-full p-4 gap-y-4">
      <section className="w-full max-h-full">
        <form className="flex flex-wrap w-full gap-4 pt-8 mb-4">
          {formData.map((form, idx) => (
            <div
              key={idx}
              className="grid w-full grid-cols-[1fr_1fr_auto] gap-4 h-fit"
            >
              <ApptBillsAutocomplete
                form={form}
                handleChange={handleChange}
                handleErrors={handleErrors}
                idx={idx}
                providerPk={providerPk}
                searchLimitsByIndex={searchLimitsByIndex}
                searchTermsByIndex={searchTermsByIndex}
                servicesOptsByIndex={servicesOptsByIndex}
                setSearchLimitsByIndex={setSearchLimitsByIndex}
                setSearchTermsByIndex={setSearchTermsByIndex}
                setServicesOptsByIndex={setServicesOptsByIndex}
              />

              <TextField
                type="number"
                required
                fullWidth
                label="Administered Service Amount"
                onChange={(e) => handleChange("price", e.target.value, idx)}
                size="small"
                value={form.price}
              />

              {!isEditionMode && (
                <Tooltip title="Remove service" placement="bottom">
                  <span>
                    <Button
                      disabled={formData.length === 1}
                      onClick={() => handleRemoveBill(idx)}
                      className="text-red-500"
                    >
                      <RemoveCircleOutlineIcon />
                    </Button>
                  </span>
                </Tooltip>
              )}
            </div>
          ))}
        </form>

        {!isEditionMode && (
          <div className="flex items-center justify-end w-full mb-4 h-fit">
            <a
              onClick={handleAddBill}
              className="text-xs cursor-pointer hover:text-blue-600"
            >
              Add another Administered Service{" "}
              <ControlPointOutlinedIcon className="ml-1 text-sm text-green-500" />
            </a>
          </div>
        )}
      </section>

      <div className="flex self-end justify-center w-full">
        <Button
          disabled={loading || !allowSave}
          onClick={handleSubmit}
          variant="contained"
          className="text-white normal-case bg-blue-800 hover:bg-blue-700"
        >
          {isEditionMode ? "Update " : "Create "} Administered Services
        </Button>
      </div>

      <ResponseModal
        title={
          openSuccess
            ? successMessage.title
            : "Error Adding Administered Service"
        }
        description={openSuccess ? successMessage.description : ""}
        errorMessage={openError ? errorArray : ""}
        isError={openError}
        openBool={openSuccess || openError}
        setOpenBool={openSuccess ? setOpenSuccess : setOpenError}
        handleCloseFunc={openSuccess ? handleOpenSuccess : undefined}
      />
    </div>
  );
}
